/**
 * Smart save queue that batches changes and saves efficiently
 */

type ChangeType = 'warband' | 'models' | 'items' | 'locations' | 'templates';

interface QueuedChange {
  type: ChangeType;
  timestamp: number;
  data: any;
}

class SaveQueue {
  private queue: Map<ChangeType, QueuedChange> = new Map();
  private saveTimer: NodeJS.Timeout | null = null;
  private isSaving: boolean = false;
  private readonly DEBOUNCE_MS = 2000; // Wait 2s after last change
  private readonly MAX_WAIT_MS = 10000; // Force save after 10s max
  private firstChangeTime: number | null = null;

  private onSaveCallback: ((changes: Map<ChangeType, QueuedChange>) => Promise<void>) | null = null;
  private onStatusChange: ((status: 'idle' | 'pending' | 'saving' | 'saved' | 'error') => void) | null = null;

  /**
   * Set callback for when save should occur
   */
  setSaveCallback(callback: (changes: Map<ChangeType, QueuedChange>) => Promise<void>) {
    this.onSaveCallback = callback;
  }

  /**
   * Set callback for status changes (for UI indicators)
   */
  setStatusCallback(callback: (status: 'idle' | 'pending' | 'saving' | 'saved' | 'error') => void) {
    this.onStatusChange = callback;
  }

  /**
   * Queue a change to be saved
   */
  queueChange(type: ChangeType, data: any) {
    // Update the queue with latest data for this type
    this.queue.set(type, {
      type,
      timestamp: Date.now(),
      data
    });

    // Track when first change occurred
    if (!this.firstChangeTime) {
      this.firstChangeTime = Date.now();
    }

    // Update status
    this.updateStatus('pending');

    // Reset debounce timer
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }

    // Check if we've exceeded max wait time
    const timeSinceFirst = Date.now() - this.firstChangeTime;
    const delay = timeSinceFirst >= this.MAX_WAIT_MS
      ? 0 // Save immediately
      : this.DEBOUNCE_MS; // Normal debounce

    // Schedule save
    this.saveTimer = setTimeout(() => {
      this.executeSave();
    }, delay);
  }

  /**
   * Force immediate save
   */
  async forceSave() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    await this.executeSave();
  }

  /**
   * Execute the batched save
   */
  private async executeSave() {
    if (this.isSaving || this.queue.size === 0) {
      return;
    }

    this.isSaving = true;
    this.updateStatus('saving');

    try {
      // Copy queue and clear it
      const changesToSave = new Map(this.queue);
      this.queue.clear();
      this.firstChangeTime = null;

      // Call the save callback
      if (this.onSaveCallback) {
        await this.onSaveCallback(changesToSave);
      }

      this.updateStatus('saved');

      // Reset to idle after 2 seconds
      setTimeout(() => {
        if (this.queue.size === 0) {
          this.updateStatus('idle');
        }
      }, 2000);

    } catch (error) {
      console.error('Save failed:', error);
      this.updateStatus('error');

      // Re-queue failed changes for retry
      // In production, implement exponential backoff here
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Update save status
   */
  private updateStatus(status: 'idle' | 'pending' | 'saving' | 'saved' | 'error') {
    if (this.onStatusChange) {
      this.onStatusChange(status);
    }
  }

  /**
   * Get current queue size (for debugging)
   */
  getQueueSize(): number {
    return this.queue.size;
  }

  /**
   * Clear the queue without saving
   */
  clear() {
    this.queue.clear();
    this.firstChangeTime = null;
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    this.updateStatus('idle');
  }
}

// Export singleton instance
export const saveQueue = new SaveQueue();
